import { DEFAULT_SORT } from './constants'

const SORT_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC'
}

const compareStrings = (left = '', right = '', direction = SORT_DIRECTION.ASC) => {
  const normalizedLeft = String(left).toLowerCase()
  const normalizedRight = String(right).toLowerCase()

  if (normalizedLeft < normalizedRight) {
    return direction === SORT_DIRECTION.DESC ? 1 : -1
  }

  if (normalizedLeft > normalizedRight) {
    return direction === SORT_DIRECTION.DESC ? -1 : 1
  }

  return 0
}

const compareNumbers = (left, right, direction = SORT_DIRECTION.ASC) => {
  const leftIsEmpty = left == null || Number.isNaN(Number(left))
  const rightIsEmpty = right == null || Number.isNaN(Number(right))

  if (leftIsEmpty && rightIsEmpty) {
    return 0
  }

  if (leftIsEmpty) {
    return 1
  }

  if (rightIsEmpty) {
    return -1
  }

  const leftValue = Number(left)
  const rightValue = Number(right)

  if (leftValue < rightValue) {
    return direction === SORT_DIRECTION.DESC ? 1 : -1
  }

  if (leftValue > rightValue) {
    return direction === SORT_DIRECTION.DESC ? -1 : 1
  }

  return 0
}

const compareBooleans = (left, right, direction = SORT_DIRECTION.ASC) => {
  const leftRank = left === true ? 1 : left === false ? 0 : -1
  const rightRank = right === true ? 1 : right === false ? 0 : -1

  if (leftRank < rightRank) {
    return direction === SORT_DIRECTION.DESC ? 1 : -1
  }

  if (leftRank > rightRank) {
    return direction === SORT_DIRECTION.DESC ? -1 : 1
  }

  return 0
}

const getComparator = (sortBy) => {
  switch (sortBy) {
    case 'siteBest':
      return (left, right, direction) => compareNumbers(left.siteBest, right.siteBest, direction)
    case 'minReproductiveUnits':
      return (left, right, direction) => compareNumbers(
        left.minReproductiveUnits,
        right.minReproductiveUnits,
        direction
      )
    case 'commonName':
      return (left, right, direction) => compareStrings(
        left.specieName || left.commonName,
        right.specieName || right.commonName,
        direction
      )
    case 'globallySensitive':
      return (left, right, direction) => compareBooleans(left.globallySensitive, right.globallySensitive, direction)
    case 'criteriaStatus':
      return (left, right, direction) => compareStrings(left.criteriaStatus, right.criteriaStatus, direction)
    case 'scientificName':
      return (left, right, direction) => compareStrings(left.scientificName, right.scientificName, direction)
    case 'taxonomicGroup':
    default:
      return (left, right, direction) => compareStrings(left.taxonomicGroup, right.taxonomicGroup, direction)
  }
}

export const sortSpecies = (speciesList = [], sortConfig = null) => {
  const activeSort = sortConfig?.sortBy ? sortConfig : DEFAULT_SORT
  const direction = activeSort.sortDirection === SORT_DIRECTION.DESC
    ? SORT_DIRECTION.DESC
    : SORT_DIRECTION.ASC
  const comparator = getComparator(activeSort.sortBy)

  return [...speciesList]
    .map((species, index) => ({ species, index }))
    .sort((left, right) => {
      const primaryComparison = comparator(left.species, right.species, direction)

      if (primaryComparison !== 0) {
        return primaryComparison
      }

      if (activeSort.sortBy !== DEFAULT_SORT.secondarySortBy) {
        const secondaryComparison = compareStrings(
          left.species.scientificName,
          right.species.scientificName,
          DEFAULT_SORT.secondarySortDirection
        )

        if (secondaryComparison !== 0) {
          return secondaryComparison
        }
      }

      return left.index - right.index
    })
    .map(({ species }) => species)
}

export const getNextSortState = (currentSort, columnId) => {
  if (!currentSort?.sortBy || currentSort.sortBy !== columnId) {
    return { sortBy: columnId, sortDirection: SORT_DIRECTION.ASC }
  }

  if (currentSort.sortDirection === SORT_DIRECTION.ASC) {
    return { sortBy: columnId, sortDirection: SORT_DIRECTION.DESC }
  }

  return DEFAULT_SORT
}
